import { rpc } from '@server/infrastructure/rpc'
import { AuthenticationRequiredError } from '@shared/libs'

import { getBearerToken } from '../../authentication'

type CurrentUserRequestOptions = { signal?: AbortSignal; timeoutMs?: number }

export const getCurrentUser = async (bearerToken?: string, options: CurrentUserRequestOptions = {}) => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const readProfile = async (signal?: AbortSignal) => {
    const response = await rpc.api.v1.users.profile.$get(
      {},
      { headers: { Authorization: `Bearer ${authorizationToken}` }, init: { signal } },
    )
    const responseStatus = response.status as number
    if (!response.ok) {
      if (responseStatus === 401) {
        throw new AuthenticationRequiredError('Failed to fetch current user: 401')
      }
      throw new Error(`Failed to fetch current user: ${responseStatus}`)
    }
    const profile = await response.json()
    return profile
  }
  const timeoutMs = options.timeoutMs
  if (timeoutMs === undefined || !Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    return readProfile(options.signal)
  }

  const controller = new AbortController()
  const timeoutError = new Error('HOME_PROFILE_TIMEOUT')
  const forwardAbort = () => controller.abort(options.signal?.reason)
  let rejectAborted!: (reason: unknown) => void
  const aborted = new Promise<never>((_resolve, reject) => {
    rejectAborted = reject
  })
  const onAbort = () => rejectAborted(controller.signal.reason)
  controller.signal.addEventListener('abort', onAbort, { once: true })
  options.signal?.addEventListener('abort', forwardAbort, { once: true })
  const timeout = setTimeout(() => controller.abort(timeoutError), timeoutMs)
  try {
    if (options.signal?.aborted) {
      forwardAbort()
    }
    // Caller cancellation bounds this read without cancelling a shared session recovery.
    return await Promise.race([readProfile(controller.signal), aborted])
  } catch (error) {
    if (controller.signal.aborted) {
      throw controller.signal.reason
    }
    throw error
  } finally {
    clearTimeout(timeout)
    controller.signal.removeEventListener('abort', onAbort)
    options.signal?.removeEventListener('abort', forwardAbort)
  }
}
